import { useMemo } from 'react'
import { OrderStepButtonsBase, OrderStepButtonsBaseComponentProps } from '../../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderButtonsBaseComponent/types'
import { SellDownloadPdfTemplateButton, SellDownloadButtonPDF, SellPdfPreview, SellViewWorkSheetButton, SellCreateWorkSheetButton, SellDownloadButtonCSV, SellQuickAccessButton, SellDownloadTags } from '../../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderSellLayout/SellButtons'
import useTwinTranslation from '../../../../../../../../../../../utils/hooks/useTwinTranslation'
import { SellInvoiceDuplicateButton, SellInvoiceRefundCreateButton } from '../../../SellInvoiceButtons'
import { getAllWorkSheetGroupStatuses } from '../../../../../../../../../../../utils/reducers/getters'
import { OrderStepButtonsDefaultBaseComponent, OrderStepButtonsNewStyleBaseComponent } from '../../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderButtonsBaseComponent'
import { OrderDivideIntoChaptersButton, OrderProductTypesButton, OrderVariablesButton } from '../../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderButtons'
import { sellInvoiceStatusInversed } from '@teinor/erp/types/company/order/sell/sellInvoice'

const SellInvoiceFinishedStepButtons: React.FC<OrderStepButtonsBaseComponentProps> = ({ data, onSubmit, ...rest }) => {
    return (
        <OrderStepButtonsDefaultBaseComponent {...{ data, onSubmit, hookFunction: useSellInvoiceFinishedStepButtonsLogic, ...rest }} className='mr-8' />
    )
}

const useSellInvoiceFinishedStepButtonsLogic = ({ data, onSubmit, isReadOnly }: OrderStepButtonsBaseComponentProps) => {
    const { t } = useTwinTranslation()
    const name = 'sell_invoice_finished_step_buttons'
    const Order = data?.header
    const workSheets = getAllWorkSheetGroupStatuses()
    const hideQuickAccessButton = !Order?.QuoteOrderId && !Order?.SellOrderId && !Order?.SellDeliveryNoteId && !Order?.SellInvoiceId && !Order?.WorkSheetId && !Order?.relatedSellOrders?.length && !Order?.relatedSellDeliveryNotes?.length && !Order?.relatedSellInvoices?.length && !Order?.relatedWorkSheets?.length && !Order?.relatedOrderRefunds?.length
    const hideCreateRefundButton = Order.status !== sellInvoiceStatusInversed['refunded'] && new Date() <= new Date(Order.maxRefundDate)

    const defaultButtons = [{ "id": "previewSellInvoice" }, { "id": "downloadSellInvoice" }, { "id": "quickAccess" }, { "id": "createWorkSheet" }, { "id": "viewWorkSheet" }, { "id": "sellInvoiceRefund" }]

    const sellInvoiceDuplicateText = t('duplicateInvoice', 'Duplicar factura')
    const sellInvoiceDownloadTagsText = t('printTags', 'Imprimir etiquetas')
    const sellInvoiceRefundCreateText = t('createSellInvoiceRefund', 'Crear devolución de la factura')
    const sellInvoiceDownloadCSVText = t('downloadCsv', 'Descargar csv')
    const sellInvoiceQuickAccessText = t('quickAccess', 'Acceso rápido')
    const sellInvoicePDFPreviewText = t('previewPdf', 'Previsualizar PDF')
    const sellInvoiceDownloadPDFTemplateText = t('downloadPdfTemplate', 'Descargar plantilla PDF')
    const sellInvoiceDownloadPDFText = t('downloadPdf', 'Descargar PDF')
    const sellInvoiceViewWorkSheetText = t('goToWorkSheet', 'Ir a la hoja de trabajo')
    const sellInvoiceCreateWorkSheetText = t('createWorkSheet', 'Crear hoja de trabajo')
    const sellInvoiceDivideIntoChaptersText = t('divideIntoChapters', 'Dividir en capítulos')
    const sellInvoiceVariablesText = t('variables', 'Variables')
    const sellInvoiceProductTypesText = t('valueProductTypes', 'Valor tipos de producto')

    const ButtonComponents: OrderStepButtonsBase = useMemo(() => {
        return {
            duplicateSell: {
                component: SellInvoiceDuplicateButton,
                extraProps: {
                    title: sellInvoiceDuplicateText,
                    id: 'duplicateSell',
                    allRowData: data,
                }
            },
            downloadSellTag: {
                component: SellDownloadTags,
                extraProps: {
                    title: sellInvoiceDownloadTagsText,
                    id: 'downloadSellTag',
                    Order,
                    typeTemplate: 'sellInvoice'
                }
            },
            sellInvoiceRefund: {
                component: SellInvoiceRefundCreateButton,
                showButton: hideCreateRefundButton,
                extraProps: {
                    title: sellInvoiceRefundCreateText,
                    id: 'sellInvoiceRefund',
                    Order,
                }
            },
            downloadSellCsv: {
                component: SellDownloadButtonCSV,
                extraProps: {
                    title: sellInvoiceDownloadCSVText,
                    id: 'downloadSellCsv',
                    Order,
                    typeTemplate: 'sellInvoice'
                }
            },
            quickAccess: {
                component: SellQuickAccessButton,
                showButton: !hideQuickAccessButton,
                extraProps: {
                    title: sellInvoiceQuickAccessText,
                    id: 'quickAccess',
                    Order,
                }
            },
            previewSellInvoice: {
                component: SellPdfPreview,
                extraProps: {
                    title: sellInvoicePDFPreviewText,
                    id: 'previewSellInvoice',
                    Order,
                    typeTemplate: 'sellInvoice'
                }
            },
            downloadSellInvoiceTemplate: {
                component: SellDownloadPdfTemplateButton,
                extraProps: {
                    title: sellInvoiceDownloadPDFTemplateText,
                    id: 'downloadSellInvoiceTemplate',
                    Order,
                    typeTemplate: 'sellInvoice',
                }
            },
            downloadSellInvoice: {
                component: SellDownloadButtonPDF,
                extraProps: {
                    title: sellInvoiceDownloadPDFText,
                    id: 'downloadSellInvoice',
                    Order,
                    typeTemplate: 'sellInvoice',
                }
            },
            viewWorkSheet: {
                component: SellViewWorkSheetButton,
                showButton: Order?.WorkSheetId,
                extraProps: {
                    title: sellInvoiceViewWorkSheetText,
                    id: 'viewWorkSheet',
                    WorkSheetId: Order?.WorkSheetId
                }
            },
            createWorkSheet: {
                component: SellCreateWorkSheetButton,
                showButton: !Order?.WorkSheetId && !!workSheets?.WorkSheetActiveStatusGroups?.length,
                extraProps: {
                    title: sellInvoiceCreateWorkSheetText,
                    id: 'createWorkSheet',
                    Order,
                    createURL: '/api/app/order/sell/workSheet/createWorkSheetBySellInvoice',
                    orderField: 'SellInvoiceId',
                    completeData: data
                }
            },
            divideIntoChaptersQuote: {
                component: OrderDivideIntoChaptersButton,
                extraProps: {
                    title: sellInvoiceDivideIntoChaptersText,
                    id: 'divideIntoChaptersQuote',
                    allRowData: data,
                    onSubmit,
                    isReadOnly
                }
            },
            variablesQuote: {
                component: OrderVariablesButton,
                extraProps: {
                    title: sellInvoiceVariablesText,
                    id: 'variablesQuote',
                    allRowData: data,
                    onSubmit,
                    isReadOnly
                }
            },
            sellProductTypes: {
                component: OrderProductTypesButton,
                extraProps: {
                    title: sellInvoiceProductTypesText,
                    id: 'sellProductTypes',
                    allRowData: data,
                    onSubmit,
                    isReadOnly
                }
            },
        }
    }, [Order, data, hideQuickAccessButton, onSubmit, sellInvoiceDivideIntoChaptersText, sellInvoiceDownloadCSVText, sellInvoiceDownloadPDFTemplateText, sellInvoiceDownloadPDFText, sellInvoiceDownloadTagsText, sellInvoiceDuplicateText, sellInvoicePDFPreviewText, sellInvoiceQuickAccessText, sellInvoiceVariablesText, sellInvoiceViewWorkSheetText, isReadOnly, sellInvoiceCreateWorkSheetText, workSheets?.WorkSheetActiveStatusGroups?.length, sellInvoiceRefundCreateText, hideCreateRefundButton, sellInvoiceProductTypesText])

    return { name, ButtonComponents, defaultButtons }
}

export const SellInvoiceFinishedStepButtonsNew: React.FC<OrderStepButtonsBaseComponentProps> = ({ data, onSubmit, ...rest }) => {
    return (
        <OrderStepButtonsNewStyleBaseComponent {...{ data, onSubmit, hookFunction: useSellInvoiceFinishedStepButtonsLogic, ...rest }} className='mr-8' />
    )
}

export default SellInvoiceFinishedStepButtons